import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { apiUrl } from '../api';
import { Icons } from '../constants';
import { Camera, Paperclip, X, Copy, Trash2, Sparkles, Check, ChevronDown, Leaf, Download } from 'lucide-react';

interface Message {
  role: 'user' | 'model';
  text: string;
  image?: string;
  links?: { web: { uri: string; title: string } }[];
  timestamp: number;
}

const CLIENT_SUGGESTIONS = [
  "Tell me about the history of arboriculture.",
  "What are recent developments in tree care?",
  "How do I identify Oak Wilt?",
  "What are the benefits of urban forestry?",
  "Explain proper pruning techniques."
];

const EMPLOYEE_SUGGESTIONS = [
  "How do I prepare for the ISA Certified Arborist exam?",
  "Explain advanced rigging techniques.",
  "What are the ANSI Z133 safety standards?",
  "How do I transition from groundworker to climber?",
  "What are the latest developments in tree care equipment?"
];

const STORAGE_KEY = 'arborist_chat_messages';

function loadMessages(mode: 'client' | 'employee'): Message[] {
  try {
    const stored = localStorage.getItem(`${STORAGE_KEY}_${mode}`);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch {}
  
  return [{
    role: 'model',
    text: mode === 'client'
      ? "Hey there! I'm Arb-E, your GreenTree AI Arborist Advisor. I live and breathe trees here in the Valley. Whether you're in Muscogee, Harris, or over in Phenix City, I can help you with pruning tips, storm-ready assessments, or local GA/AL tree ordinances. Got wood? I've got the answers! How can I help you today?"
      : "Hey team! I'm Arb-E, your Master Arborist Mentor. I'm here to help you advance your career, master ANSI Z133 safety standards, and prep for your ISA Certification. Got wood? Let's get to work! What do you want to learn today?",
    timestamp: Date.now(),
  }];
}

function persistMessages(mode: 'client' | 'employee', msgs: Message[]) {
  try {
    localStorage.setItem(`${STORAGE_KEY}_${mode}`, JSON.stringify(msgs));
  } catch {}
}

// Simple markdown-to-HTML renderer (bold, italic, code, lists, links, headings)
function renderMarkdown(text: string): string {
  let html = text
    // Escape HTML
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // Code blocks
    .replace(/```(\w*)\n([\s\S]*?)```/g, '<pre class="bg-stone-100 rounded-xl p-4 my-3 overflow-x-auto text-xs font-mono"><code>$2</code></pre>')
    // Inline code
    .replace(/`([^`]+)`/g, '<code class="bg-stone-100 text-palm px-1.5 py-0.5 rounded text-xs font-mono">$1</code>')
    // Bold
    .replace(/\*\*([^*]+)\*\*/g, '<strong class="font-black">$1</strong>')
    // Italic
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    // Headings
    .replace(/^### (.+)$/gm, '<h4 class="font-black text-sm mt-4 mb-2">$1</h4>')
    .replace(/^## (.+)$/gm, '<h3 class="font-black text-base mt-5 mb-2">$1</h3>')
    // Unordered lists
    .replace(/^[*-] (.+)$/gm, '<li class="ml-4 list-disc text-sm">$1</li>')
    // Ordered lists
    .replace(/^\d+\. (.+)$/gm, '<li class="ml-4 list-decimal text-sm">$1</li>')
    // Links
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer" class="text-emerald-600 underline hover:text-emerald-800">$1</a>')
    // Line breaks
    .replace(/\n\n/g, '</p><p class="mb-2">')
    .replace(/\n/g, '<br/>');

  return `<p class="mb-2">${html}</p>`;
}

const ArboristChat: React.FC<{ address?: string; mode?: 'client' | 'employee' }> = ({ address, mode = 'client' }) => {
  const [messages, setMessages] = useState<Message[]>(() => loadMessages(mode));
  const [input, setInput] = useState('');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingText, setStreamingText] = useState('');
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    persistMessages(mode, messages);
  }, [messages, mode]);

  const scrollToBottom = useCallback((smooth = false) => {
    messagesEndRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  useEffect(() => {
    scrollToBottom(true);
  }, [messages, streamingText, scrollToBottom]);

  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    setShowScrollBtn(scrollHeight - scrollTop - clientHeight > 200);
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate size: 10MB max
      if (file.size > 10 * 1024 * 1024) {
        setMessages(prev => [...prev, { role: 'model', text: "📎 That file is over 10MB — please try a smaller image or PDF.", timestamp: Date.now() }]);
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCopy = async (text: string, index: number) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(index);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // Fallback for older browsers
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopiedId(index);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const handleClear = () => {
    if (messages.length <= 1) return;
    const initial = loadMessages(mode);
    setMessages(initial);
  };

  const handleExport = () => {
    const text = messages
      .filter(m => m.role !== 'model' || m.timestamp !== loadMessages(mode)[0].timestamp)
      .map(m => `${m.role === 'user' ? 'You' : 'Arb-E'}: ${m.text}`)
      .join('\n\n---\n\n');
    
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `arborai-chat-${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSend = async (text: string = input) => {
    if ((!text.trim() && !selectedImage) || isStreaming) return;

    // Focus input after send
    setTimeout(() => inputRef.current?.focus(), 50);

    const userMsg = text;
    const image = selectedImage;
    setInput('');
    setSelectedImage(null);

    const userMessage: Message = { role: 'user', text: userMsg, image: image || undefined, timestamp: Date.now() };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);

    setIsStreaming(true);
    setStreamingText('');

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const response = await fetch(apiUrl('/api/ai/chat/stream'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMsg,
          image: image,
          history: messages.map(m => ({ role: m.role, parts: [{ text: m.text }] })),
          model: 'gemini-3.6-flash',
          systemInstruction: mode === 'employee'
            ? 'You are Arb-E, an expert arborist mentor helping tree care professionals advance their careers. Use markdown formatting for readability — headings, bold for key terms, lists for procedures.'
            : 'You are Arb-E, an expert arborist advisor helping homeowners and property managers with tree care in the Chattahoochee Valley (Columbus GA, Phenix City AL area). Be warm, knowledgeable, and thorough. Use markdown formatting for readability — headings, bold for key terms, lists for steps.'
        }),
        signal: controller.signal,
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const reader = response.body?.getReader();
      if (!reader) throw new Error('No reader');

      const decoder = new TextDecoder();
      let buffer = '';
      let fullText = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('event: ')) continue;
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.text) {
                fullText += data.text;
                setStreamingText(fullText);
              }
            } catch {}
          }
        }
      }

      setMessages(prev => [...prev, {
        role: 'model',
        text: fullText || "Hmm, I didn't catch that. Could you try again?",
        timestamp: Date.now(),
      }]);
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        setMessages(prev => [...prev, { role: 'model', text: streamingText || '[Response cancelled]', timestamp: Date.now() }]);
      } else {
        setMessages(prev => [...prev, { role: 'model', text: "Signal lost in the canopy. Let's try again in a second!", timestamp: Date.now() }]);
      }
    } finally {
      setIsStreaming(false);
      setStreamingText('');
      abortRef.current = null;
    }
  };

  const handleStop = () => {
    abortRef.current?.abort();
  };

  const hasHistory = messages.length > 1;

  return (
    <div className="bg-white rounded-[32px] border border-stone-200 shadow-sm flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="bg-bark p-5 text-white flex items-center justify-between shrink-0">
        <div className="flex items-center space-x-3">
          <div className="w-11 h-11 bg-white/10 rounded-2xl flex items-center justify-center text-2xl overflow-hidden shrink-0">
            <img 
              src="/logo.png" 
              alt="Arb-E" 
              className="w-full h-full object-contain p-1"
              onError={(e) => {
                e.currentTarget.src = "https://api.dicebear.com/7.x/bottts/svg?seed=Arb-E&backgroundColor=A8D672";
              }}
            />
          </div>
          <div className="min-w-0">
            <h4 className="font-black text-sm tracking-tight truncate">Arb-E</h4>
            <p className="text-[10px] opacity-60 uppercase font-black tracking-[0.2em]">Certified AI Arborist</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {hasHistory && (
            <>
              <button
                onClick={handleExport}
                title="Export chat"
                className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition"
              >
                <Download size={14} />
              </button>
              <button
                onClick={handleClear}
                title="Clear chat"
                className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition"
              >
                <Trash2 size={14} />
              </button>
            </>
          )}
          <div className="text-right ml-2">
            <p className="text-[10px] font-black uppercase text-gold leading-tight">Professional</p>
            <p className="text-[10px] font-black uppercase text-gold leading-tight">Tree Care</p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 space-y-5 bg-stone-50 custom-scrollbar"
      >
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`group relative max-w-[90%] sm:max-w-[85%] p-4 sm:p-5 rounded-[24px] ${
              m.role === 'user'
                ? 'bg-gecko text-white rounded-br-md shadow-lg shadow-gecko/10'
                : 'bg-white text-bark border border-stone-200 shadow-sm rounded-bl-md'
            }`}>
              {m.image && (
                <img src={m.image} alt="User attachment" className="rounded-xl mb-3 max-w-full max-h-64 object-cover" />
              )}

              {m.role === 'model' && i === 0 ? (
                // Welcome message: plain text, no markdown
                <p className="text-sm leading-relaxed whitespace-pre-wrap font-medium">{m.text}</p>
              ) : m.role === 'model' ? (
                // AI responses: render markdown
                <div
                  className="text-sm leading-relaxed prose-sm prose-stone max-w-none [&_p]:mb-2 [&_ul]:my-2 [&_ol]:my-2 [&_li]:ml-4 [&_li]:text-sm [&_pre]:my-3 [&_strong]:font-black [&_a]:text-emerald-600 [&_a]:underline"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(m.text) }}
                />
              ) : (
                <p className="text-sm leading-relaxed whitespace-pre-wrap font-medium">{m.text}</p>
              )}

              {m.links && m.links.length > 0 && (
                <div className="mt-4 pt-4 border-t border-stone-100 space-y-2">
                  <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest">Sources:</p>
                  {m.links.map((link: any, li: number) => (
                    link.web && (
                      <a
                        key={li}
                        href={link.web.uri}
                        target="_blank"
                        rel="noreferrer"
                        className="block text-xs text-palm hover:text-gecko font-black truncate transition-colors"
                      >
                        🌳 {link.web.title || link.web.uri}
                      </a>
                    )
                  ))}
                </div>
              )}

              {/* Copy button (model messages only) */}
              {m.role === 'model' && i > 0 && (
                <button
                  onClick={() => handleCopy(m.text, i)}
                  className="absolute -bottom-1 right-2 translate-y-full opacity-0 group-hover:opacity-100 transition-opacity bg-white border border-stone-200 rounded-lg p-1.5 shadow-sm hover:bg-stone-50"
                  title="Copy response"
                >
                  {copiedId === i ? <Check size={12} className="text-gecko" /> : <Copy size={12} className="text-stone-400" />}
                </button>
              )}
            </div>
          </div>
        ))}

        {/* Streaming message */}
        {isStreaming && (
          <div className="flex justify-start">
            <div className="bg-white border border-stone-200 shadow-sm rounded-[24px] rounded-bl-md p-4 sm:p-5 max-w-[90%] sm:max-w-[85%]">
              {streamingText ? (
                <div
                  className="text-sm leading-relaxed prose-sm prose-stone max-w-none [&_p]:mb-2 [&_ul]:my-2 [&_ol]:my-2 [&_li]:ml-4 [&_li]:text-sm [&_pre]:my-3 [&_strong]:font-black [&_a]:text-emerald-600 [&_a]:underline"
                  dangerouslySetInnerHTML={{ __html: renderMarkdown(streamingText) }}
                />
              ) : (
                <div className="flex items-center space-x-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gecko/40 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-gecko/40 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-gecko/40 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                  <span className="text-xs text-stone-400 font-medium">Arb-E is typing...</span>
                </div>
              )}
              <button
                onClick={handleStop}
                className="mt-2 text-xs text-stone-400 hover:text-red-500 font-bold transition"
              >
                Stop generating
              </button>
            </div>
            <div ref={messagesEndRef} />
          </div>
        )}

        {!isStreaming && <div ref={messagesEndRef} />}
      </div>

      {/* Scroll-to-bottom FAB */}
      {showScrollBtn && (
        <div className="absolute bottom-[120px] left-1/2 -translate-x-1/2 z-10">
          <button
            onClick={() => scrollToBottom(true)}
            className="bg-white border border-stone-200 shadow-lg rounded-full p-2 hover:bg-stone-50 transition"
          >
            <ChevronDown size={16} className="text-stone-500" />
          </button>
        </div>
      )}

      {/* Input area */}
      <div className="p-3 sm:p-4 bg-white border-t border-stone-100 flex flex-col space-y-2 shrink-0 relative">
        {/* Suggestions */}
        <div className="flex overflow-x-auto gap-2 pb-1 custom-scrollbar no-scrollbar">
          {(mode === 'employee' ? EMPLOYEE_SUGGESTIONS : CLIENT_SUGGESTIONS).map((suggestion, idx) => (
            <button
              key={idx}
              onClick={() => handleSend(suggestion)}
              disabled={isStreaming}
              className="whitespace-nowrap px-3 py-1.5 bg-stone-50 border border-stone-200 rounded-full text-xs font-bold text-stone-600 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200 transition flex-shrink-0 disabled:opacity-40"
            >
              {suggestion}
            </button>
          ))}
        </div>

        {selectedImage && (
          <div className="relative w-16 h-16">
            <img src={selectedImage} alt="Preview" className="w-full h-full object-cover rounded-xl border border-stone-200" />
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 shadow"
            >
              <X size={10} />
            </button>
          </div>
        )}

        <div className="flex space-x-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
            accept="image/*, application/pdf"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isStreaming}
            className="p-3 sm:p-4 bg-stone-100 text-stone-600 rounded-2xl hover:bg-stone-200 transition disabled:opacity-40 shrink-0"
            title="Attach image or PDF"
          >
            <Paperclip size={18} />
          </button>
          <input
            ref={inputRef}
            type="text"
            placeholder="Ask Arb-E anything..."
            className="flex-1 min-w-0 p-3 sm:p-4 bg-stone-50 rounded-2xl border border-stone-200 outline-none focus:ring-2 focus:ring-emerald-500/50 transition font-medium text-sm"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            disabled={isStreaming}
          />
          <button
            onClick={() => handleSend()}
            disabled={isStreaming || (!input.trim() && !selectedImage)}
            className="p-3 sm:p-4 bg-emerald-500 text-white rounded-2xl hover:bg-emerald-600 transition shadow-lg shadow-emerald-500/20 disabled:opacity-40 disabled:shadow-none shrink-0"
            title="Send message"
          >
            {isStreaming ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            )}
          </button>
        </div>

        <p className="text-[10px] text-stone-400 text-center">
          Arb-E can make mistakes. Verify important information with a certified arborist.
        </p>
      </div>
    </div>
  );
};

export default ArboristChat;
